import React from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { faClose, faUsers } from "@fortawesome/free-solid-svg-icons"
import { ButtonIcon } from "../../components/Button/Button"
import List from "../../components/List/List"
import { AdminListCard, AdminListMetricItem } from "./components/AdminListCard"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { formatDateToString } from "../../Objects/Generics/Helps"
import { EResellerCommissionDurationType } from "../../Enum/EResellerCommissionDurationType"
import type { ResellerClient } from "../../hooks/Reseller/useGetResellerClients"

interface ResellerClientsPopupProps {
  clients: ResellerClient[]
  onClose: () => void
}

export const ResellerClientsPopup: React.FC<ResellerClientsPopupProps> = ({ clients, onClose }) => {
  const { t } = useTranslation("admin")

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={`${defaultStyle.alert} max-h-[85vh] overflow-y-auto`}>
            <TitlePageResponsiveIconSmall
              title={t("reseller_clients_title")}
              icon={faUsers}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.resellerClientsPopup.button.onClose"
                />
              }
            />

            <div className="mt-2 p-2">
              <List
                showFilterStatus={false}
                messageCount={t("reseller_clients_count")}
                messageEmpty={t("reseller_clients_empty")}
                nameFindSearch={t("reseller_clients_search")}
                handleOnClick={() => {}}
                data={clients.map((client, index) => ({
                  id: client.id,
                  index,
                  title: client.companyName || "—",
                  linkImage: "",
                  dataFilter: [client.companyName, client.subscriptionStatus],
                  children: (
                    <AdminListCard
                      title={client.companyName || "—"}
                      subtitle={client.subscriptionStatus}
                      badge={
                        client.isCommissionActive ? t("reseller_commission_active") : t("reseller_commission_ended")
                      }
                      badgeClass={
                        client.isCommissionActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"
                      }
                      cardClass="rounded-xl border-2 border-slate-200 bg-slate-50 p-3"
                    >
                      <div className="gap-2">
                        <AdminListMetricItem
                          label={t("reseller_column_since")}
                          value={formatDateToString(client.linkedAtUtc)}
                        />

                        <AdminListMetricItem
                          label={t("reseller_commission_plan_percentage_placeholder")}
                          value={`${client.commissionPercentageSnapshot}%`}
                          accent="text-orange-700"
                        />

                        <AdminListMetricItem
                          label={t("reseller_commission_plan_duration_type")}
                          value={
                            client.commissionDurationTypeSnapshot === EResellerCommissionDurationType.Recurring
                              ? t("reseller_commission_plan_duration_recurring")
                              : t("reseller_duration_until", {
                                  date: client.commissionEndsAtUtc
                                    ? formatDateToString(client.commissionEndsAtUtc)
                                    : "—",
                                })
                          }
                        />
                      </div>
                    </AdminListCard>
                  ),
                }))}
              />
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
